import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../api';
import './AiCosting.css';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  ReferenceLine
} from 'recharts';
import {
  RefreshCw,
  ExternalLink,
  Download,
  Search,
  Building2,
  Cpu,
  Layers,
  Users,
  DollarSign,
  Coins,
  TrendingUp,
  Eye,
  X,
  Calendar,
  CheckCircle2,
  Info,
  ChevronDown,
  FileText,
  FileSpreadsheet,
  Wallet,
  CreditCard,
  PiggyBank,
  Clock,
  ArrowUpDown
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const SERVICE_COLORS = ['#168cf5', '#10b981', '#f59e0b', '#7557ee', '#64748b'];

const formatIndianNumber = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '0';
  const numStr = Math.round(Number(val)).toString();
  if (numStr.length <= 3) return numStr;
  const lastThree = numStr.substring(numStr.length - 3);
  const otherNumbers = numStr.substring(0, numStr.length - 3);
  return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree;
};

const formatUSD = (val) => {
  const num = Number(val || 0);
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatINR = (val) => {
  const num = Number(val || 0);
  return '₹ ' + num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatRangeLabel = (from, to) => {
  if (!from || !to) return 'All time';
  const fmt = (d) => {
    const dt = new Date(d + 'T00:00:00');
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
  };
  return `${fmt(from)} - ${fmt(to)}`;
};

const BeyondPresenceCosting = () => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Export dropdown state
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);

  // Core Data States
  const [summary, setSummary] = useState(null);
  const [uniCostingData, setUniCostingData] = useState({ rows: [], total_summary: null });
  const [universities, setUniversities] = useState([]);
  const [models, setModels] = useState([]);
  const [services, setServices] = useState([]);
  const [students, setStudents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [beyondPresenceInfo, setBeyondPresenceInfo] = useState(null);
  const [wallet, setWallet] = useState(null);

  // Active View States
  const [activeTab, setActiveTab] = useState('university'); // 'university' | 'model' | 'service' | 'student' | 'logs'
  const [search, setSearch] = useState('');
  const [selectedBottomUni, setSelectedBottomUni] = useState('');

  // Filters (defaults to the current month, no hardcoded dates)
  const today = new Date();
  const monthStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  const monthEnd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()).padStart(2, '0')}`;
  const [filters, setFilters] = useState({
    from: monthStart,
    to: monthEnd,
    university: '',
    feature: ''
  });

  // Table Sorting States
  const [sortField, setSortField] = useState('minutes_used');
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' | 'desc'

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // University Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalUniversity, setModalUniversity] = useState(null);

  // Fetch Live Beyond Presence Metadata
  const loadBeyondPresenceMeta = async () => {
    try {
      const res = await api.get('/admin/ai-costing/openrouter-info');
      setBeyondPresenceInfo(res.data);
    } catch (err) {
      console.warn('Could not fetch Beyond Presence info:', err);
    }
  };

  // Fetch live Beyond Presence wallet (credits / used / remaining)
  const loadWallet = () => {};

  // Sync pricing directly with Beyond Presence API
  const handleSyncPricing = async () => {
    setSyncing(true);
    setSyncSuccess(false);
    try {
      await loadDashboard();
      setSyncSuccess(true);
      setTimeout(() => setSyncSuccess(false), 2000);
    } catch (err) {
      console.error('Refresh failed:', err);
    } finally {
      setSyncing(false);
    }
  };

  // Main Dashboard Data Loader
  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.from) params.from_date = filters.from;
      if (filters.to) params.to_date = filters.to;
      if (filters.university) params.university_id = filters.university;
      if (filters.feature) params.agent_type = filters.feature;

      const results = await Promise.allSettled([
        api.get('/admin/ai-costing/beyond-presence/summary', { params }),
        api.get('/admin/ai-costing/beyond-presence/universities-costing', { params }),
        api.get('/admin/ai-costing/universities'),
        api.get('/admin/ai-costing/beyond-presence/agents', { params }),
        api.get('/admin/ai-costing/beyond-presence/services', { params }),
        api.get('/admin/ai-costing/beyond-presence/students-costing', { params }),
        api.get('/admin/ai-costing/beyond-presence/usage-logs', { params })
      ]);

      const [sumRes, uniCostRes, uniListRes, modRes, srvRes, stuRes, logRes] = results;

      if (sumRes.status === 'fulfilled') {
        setSummary(sumRes.value.data);
      }
      if (uniCostRes.status === 'fulfilled') {
        const data = uniCostRes.value.data;
        setUniCostingData(Array.isArray(data) ? { rows: data, total_summary: null } : (data || { rows: [], total_summary: null }));
      }
      const uList = uniListRes.status === 'fulfilled' ? (uniListRes.value.data || []) : [];
      setUniversities(uList);
      if (modRes.status === 'fulfilled') {
        setModels(modRes.value.data || []);
      }
      if (srvRes.status === 'fulfilled') {
        setServices(srvRes.value.data || []);
      }
      if (stuRes.status === 'fulfilled') {
        setStudents(stuRes.value.data || []);
      }
      if (logRes.status === 'fulfilled') {
        setLogs(logRes.value.data || []);
      }

      if (!selectedBottomUni && uList.length > 0) {
        setSelectedBottomUni(uList[0].id);
      }

      // If critical summary failed or all failed, surface error message
      const allFailed = results.every((r) => r.status === 'rejected');
      if (allFailed || sumRes.status === 'rejected') {
        const err = sumRes.status === 'rejected' ? sumRes.reason : results.find((r) => r.status === 'rejected')?.reason;
        setError('Unable to load live costing data. ' + (err?.response?.data?.detail || err?.message || 'Server error'));
      }
    } catch (err) {
      console.error(err);
      setError('Unable to load live costing data. ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBeyondPresenceMeta();
    loadWallet();
  }, []);

  // Close export dropdown on outside click
  useEffect(() => {
    const closeMenu = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, []);

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDatePreset = (preset) => {
    if (preset === 'current_month') {
      setFilters((prev) => ({ ...prev, from: monthStart, to: monthEnd }));
    } else if (preset === 'all_time') {
      setFilters((prev) => ({ ...prev, from: '', to: '' }));
    }
  };

  const openUniversityModal = (uniRow) => {
    setModalUniversity(uniRow);
    setModalOpen(true);
  };

  // CSV Export
  const exportCSV = () => {
    const data = logs;
    if (!data.length) {
      alert('No usage logs to export. Use “PDF Report” to download the full dashboard report.');
      return;
    }
    const headers = [
      'Session ID',
      'Date & Time',
      'University Name',
      'Student Name',
      'User Type',
      'AI Feature',
      'Sessions Count',
      'Duration (Minutes)'
    ];
    const csvRows = [
      headers,
      ...data.map((x) => [
        x.id,
        x.created_at ? new Date(x.created_at).toLocaleString() : '',
        x.university_name || 'Independent Students',
        x.student_name || '',
        x.user_type || 'STUDENT',
        x.service_name || 'AI Interview',
        x.total_sessions || 1,
        (x.duration_minutes ?? 0).toFixed(1)
      ])
    ]
      .map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvRows], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `beyond-presence-usage-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  // PDF Report Export
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = doc.getPageWidth();
    const pageHeight = doc.getPageHeight();
    const margin = 30;
    const period = formatRangeLabel(filters.from, filters.to);

    // Header band
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageWidth, 64, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    doc.text('Beyond Presence Usage & Session Report', margin, 26);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(200, 215, 235);
    doc.text(`Period: ${period}   |   Generated: ${new Date().toLocaleString()}`, margin, 44);

    let y = 84;

    const sectionTitle = (title) => {
      if (y > pageHeight - 60) {
        doc.addPage();
        y = margin + 10;
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(30, 41, 59);
      doc.text(title, margin, y);
      y += 6;
    };

    const addTable = (head, body, opts = {}) => {
      const table = autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head,
        body,
        theme: 'grid',
        headStyles: { fillColor: [22, 140, 245], textColor: 255, fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8, textColor: [30, 41, 59] },
        alternateRowStyles: { fillColor: [245, 248, 252] },
        styles: { cellPadding: 4, lineColor: [226, 232, 240], lineWidth: 0.5 },
        didDrawPage: () => {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(148, 163, 184);
          doc.text(`Beyond Presence Usage & Session Report — ${period}`, margin, pageHeight - 16);
          doc.text(`Page ${doc.getNumberOfPages()}`, pageWidth - margin, pageHeight - 16, {
            align: 'right'
          });
        },
        ...opts
      });
      y = table.finalY + 18;
    };

    // 1. Summary KPIs
    if (summary) {
      sectionTitle('Summary KPIs');
      addTable(
        [['Metric', 'Value']],
        [
          ['Total Sessions', formatIndianNumber(summary.total_sessions)],
          ['Active Students', formatIndianNumber(summary.active_students)],
          ['Active Universities', formatIndianNumber(summary.active_universities)],
          ['Total Minutes Used', `${formatIndianNumber(summary.minutes_used)} mins`],
          ['Total Minutes Remaining', `${formatIndianNumber(summary.minutes_remaining)} mins`],
          ['AI Interview Minutes', `${formatIndianNumber(summary.interview_minutes)} mins`],
          ['AI Counsellor Minutes', `${formatIndianNumber(summary.counsellor_minutes)} mins`],
          ['AI Tutor Minutes', `${formatIndianNumber(summary.tutor_minutes)} mins`]
        ],
        { columnStyles: { 0: { fontStyle: 'bold' } } }
      );
    }

    // 2. University Wise Usage
    const uniRows = uniCostingData.rows || [];
    if (uniRows.length > 0) {
      sectionTitle('University Wise Usage');
      addTable(
        ['#', 'University', 'Students', 'Total Sessions', 'Minutes Used', 'Minutes Remaining'],
        uniRows.map((r, i) => [
          String(i + 1),
          r.university_name,
          r.students_count,
          formatIndianNumber(r.total_sessions),
          `${(r.minutes_used ?? 0).toFixed(1)} mins`,
          `${(r.minutes_remaining ?? 0).toFixed(1)} mins`
        ])
      );
    }

    // 3. Feature Breakdown
    if (services.length > 0) {
      sectionTitle('Feature Breakdown');
      addTable(
        ['Feature', 'Total Sessions', 'Minutes Used', '% of Usage'],
        services.map((s) => [
          s.service_name,
          formatIndianNumber(s.total_sessions),
          `${(s.minutes_used ?? 0).toFixed(1)} mins`,
          `${s.percentage}%`
        ])
      );
    }

    // 4. Student Wise Usage
    if (students.length > 0) {
      sectionTitle('Student Wise Usage');
      addTable(
        ['#', 'Student', 'University', 'Sessions', 'Interview (mins)', 'Counsellor (mins)', 'Tutor (mins)', 'Total Used (mins)', 'Remaining (mins)'],
        students.slice(0, 50).map((st, i) => [
          String(i + 1),
          st.student_name,
          st.university_name,
          formatIndianNumber(st.total_sessions),
          (st.interview_minutes ?? 0).toFixed(1),
          (st.counsellor_minutes ?? 0).toFixed(1),
          (st.tutor_minutes ?? 0).toFixed(1),
          (st.minutes_used ?? 0).toFixed(1),
          (st.minutes_remaining ?? 0).toFixed(1)
        ])
      );
    }

    // 5. Detailed usage logs
    if (logs.length > 0) {
      sectionTitle('Detailed Usage Logs');
      addTable(
        ['Timestamp', 'University', 'Student', 'Feature', 'Sessions', 'Duration (mins)'],
        logs.slice(0, 100).map((l) => [
          l.created_at ? new Date(l.created_at).toLocaleString() : '—',
          l.university_name,
          l.student_name || '—',
          l.service_name,
          formatIndianNumber(l.total_sessions || 1),
          `${(l.duration_minutes ?? 0).toFixed(1)} mins`
        ])
      );
    }

    // 6. No-data fallback note
    if (!summary && uniRows.length === 0 && services.length === 0) {
      sectionTitle('No data available for the selected period.');
    }

    doc.save(`beyond-presence-report-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // Sorting helper for tables
  const sortRows = (rows, field, order) => {
    if (!field || !rows || !rows.length) return rows || [];
    return [...rows].sort((a, b) => {
      let va = a[field];
      let vb = b[field];
      if (typeof va === 'string' && isNaN(Date.parse(va))) {
        va = va.toLowerCase();
        vb = (vb || '').toLowerCase();
        return order === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      if (typeof va === 'string' && !isNaN(Date.parse(va))) {
        const da = new Date(va).getTime();
        const db = new Date(vb || 0).getTime();
        return order === 'asc' ? da - db : db - da;
      }
      va = Number(va) || 0;
      vb = Number(vb) || 0;
      return order === 'asc' ? va - vb : vb - va;
    });
  };

  // Filtered University Rows
  const filteredUniRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = uniCostingData.rows || [];
    if (q) {
      rows = rows.filter((r) =>
        (r.university_name || '').toLowerCase().includes(q)
      );
    }
    return sortRows(rows, sortField, sortOrder);
  }, [uniCostingData.rows, search, sortField, sortOrder]);

  // Filtered Model Rows
  const filteredModelRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return models;
    return models.filter(
      (m) =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.id || '').toLowerCase().includes(q) ||
        (m.provider || '').toLowerCase().includes(q)
    );
  }, [models, search]);

  // Filtered Service Rows
  const filteredServiceRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return services;
    return services.filter((s) => (s.service_name || '').toLowerCase().includes(q));
  }, [services, search]);

  // Filtered Student Rows
  const filteredStudentRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = students || [];
    if (q) {
      rows = rows.filter(
        (st) =>
          (st.student_name || '').toLowerCase().includes(q) ||
          (st.university_name || '').toLowerCase().includes(q)
      );
    }
    return sortRows(rows, sortField, sortOrder);
  }, [students, search, sortField, sortOrder]);

  // Filtered Usage Logs
  const filteredLogRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter(
      (l) =>
        (l.university_name || '').toLowerCase().includes(q) ||
        (l.student_name || '').toLowerCase().includes(q) ||
        (l.service_name || '').toLowerCase().includes(q) ||
        (l.model_name || '').toLowerCase().includes(q) ||
        (l.provider || '').toLowerCase().includes(q)
    );
  }, [logs, search]);

  // Modal logs calculation
  const modalStats = useMemo(() => {
    if (!modalUniversity) return { sessions: 0, logs: [] };
    const uId = modalUniversity.id || modalUniversity.university_id;
    const uName = (modalUniversity.university_name || '').trim().toLowerCase();
    const uLogs = logs.filter((l) => {
      if (uId && l.university_id && Number(l.university_id) === Number(uId)) return true;
      if (uName && (l.university_name || '').trim().toLowerCase() === uName) return true;
      return false;
    });
    return { sessions: uLogs.length, logs: uLogs };
  }, [modalUniversity, logs]);

  // Bottom Card 1: Selected University AI Services
  const bottomUniServices = useMemo(() => {
    const uId = Number(selectedBottomUni);
    const targetUni = universities.find((u) => Number(u.id) === uId);
    const targetName = (targetUni?.name || '').trim().toLowerCase();
    const uLogs = logs.filter((l) => {
      if (uId && l.university_id && Number(l.university_id) === uId) return true;
      if (targetName && (l.university_name || '').trim().toLowerCase() === targetName) return true;
      return false;
    });
    const map = {};
    uLogs.forEach((l) => {
      const sName = l.service_name || 'General';
      if (!map[sName]) {
        map[sName] = {
          service_name: sName,
          models: new Set(),
          sessions: 0,
          minutes: 0
        };
      }
      map[sName].models.add(l.model_name || l.actual_model_id);
      map[sName].sessions += Number(l.total_sessions || 1);
      map[sName].minutes = Math.round((map[sName].minutes + Number(l.duration_minutes || 0)) * 10) / 10;
    });
    return Object.values(map);
  }, [selectedBottomUni, logs, universities]);

  // Bottom Card 2: Student Usage
  const bottomStudents = useMemo(() => {
    return students.slice(0, 5);
  }, [students]);

  // Bottom Card 3: Model Usage
  const bottomModels = useMemo(() => {
    return models.slice(0, 5);
  }, [models]);

  // Feature Minutes Breakdown for Donut Chart
  const featureMinutesData = useMemo(() => {
    const total = Number(summary?.total_minutes_used || 0);
    const intM = Number(summary?.interview_minutes || 0);
    const couM = Number(summary?.counsellor_minutes || 0);
    const tutM = Number(summary?.tutor_minutes || 0);

    const calcPct = (v) => (total > 0 ? ((v / total) * 100).toFixed(1) : '0');

    return [
      { name: 'AI Interview', minutes: intM, percentage: calcPct(intM), color: '#168cf5' },
      { name: 'AI Counsellor', minutes: couM, percentage: calcPct(couM), color: '#a855f7' },
      { name: 'AI Tutor', minutes: tutM, percentage: calcPct(tutM), color: '#10b981' }
    ];
  }, [summary]);

  // Donut chart total cost
  const totalCostUsdValue = summary?.total_cost_usd ?? 0;

  return (
    <div className="ai-costing-dashboard">
      {/* Top Beyond Presence Live Banner */}
      <div className="beyondpresence-status-strip">
        <div className="beyondpresence-strip-left">
          <span className="live-pulse"></span>
          <span className="beyondpresence-title">Beyond Presence Live Integration</span>
          <span className="badge-workspace">
            Workspace: <strong>default</strong>
          </span>
          <span className="badge-fee">{beyondPresenceInfo?.fee_percentage != null ? `${beyondPresenceInfo.fee_percentage}% Platform Fee` : 'Platform Fee'}</span>
          {beyondPresenceInfo?.total_models && (
            <span className="badge-models">{beyondPresenceInfo.total_models} Models Available</span>
          )}
        </div>
        <div className="beyondpresence-strip-right">
          <button
            className={`sync-pricing-btn ${syncing ? 'spinning' : ''}`}
            onClick={handleSyncPricing}
            disabled={syncing}
            title="Fetch live model pricing from Beyond Presence API"
          >
            <RefreshCw size={14} className={syncing ? 'spin-icon' : ''} />
            {syncing ? 'Syncing...' : 'Sync Live Pricing'}
          </button>
          <a
            href="https://bey.studio"
            target="_blank"
            rel="noopener noreferrer"
            className="beyondpresence-link-btn"
          >
            Beyond Presence Dashboard <ExternalLink size={13} />
          </a>
        </div>
      </div>

      {syncSuccess && (
        <div className="toast-success">
          <CheckCircle2 size={16} /> Live Beyond Presence model pricing synchronized successfully.
        </div>
      )}

      {/* Header Section */}
      <div className="header-section">
        <div className="header-titles">
          <h1 className="costing-title">Beyond Presence Costing</h1>
          <p className="costing-subtitle">
            Track AI usage, tokens, and actual cost across universities, students and AI services.
          </p>
        </div>

        {/* Top Filters Bar */}
        <div className="filters-bar">
          <div className="date-range-group">
            <span className="filter-icon">
              <Calendar size={14} />
            </span>
            <input
              type="date"
              name="from"
              value={filters.from}
              onChange={handleFilterChange}
              className="filter-date"
              title="Start Date"
            />
            <span className="date-sep">to</span>
            <input
              type="date"
              name="to"
              value={filters.to}
              onChange={handleFilterChange}
              className="filter-date"
              title="End Date"
            />
            <button
              type="button"
              className={`preset-btn ${filters.from === monthStart && filters.to === monthEnd ? 'active' : ''}`}
              onClick={() => handleDatePreset('current_month')}
              title={`Quick Select: ${formatRangeLabel(monthStart, monthEnd)}`}
            >
              Current Month
            </button>
          </div>

          <div className="select-group">
            <select
              name="university"
              value={filters.university}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Universities</option>
              {universities.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>

          <div className="select-group">
            <select
              name="feature"
              value={filters.feature}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Features</option>
              {Array.from(new Set(['AI Interview', 'AI Counsellor', 'AI Tutor', ...models.map((m) => m.name)].filter(Boolean))).map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>

          <div className="export-menu-wrap" ref={exportMenuRef}>
            <button
              onClick={() => setExportMenuOpen((o) => !o)}
              className="export-action-btn"
              title="Export report"
            >
              <Download size={14} /> Export
              <ChevronDown size={13} className={`export-caret ${exportMenuOpen ? 'open' : ''}`} />
            </button>
            {exportMenuOpen && (
              <div className="export-menu">
                <button
                  className="export-menu-item"
                  onClick={() => {
                    setExportMenuOpen(false);
                    exportPDF();
                  }}
                  title="Download a formatted PDF report of the whole dashboard"
                >
                  <FileText size={13} /> PDF Report
                </button>
                <button
                  className="export-menu-item"
                  onClick={() => {
                    setExportMenuOpen(false);
                    exportCSV();
                  }}
                  title="Download raw usage logs as CSV"
                >
                  <FileSpreadsheet size={13} /> CSV Export
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="costing-error-banner">{error}</div>}

      {/* Main Content Area */}
      <div className={`costing-body ${loading ? 'loading-mask' : ''}`}>
        {/* Beyond Presence Wallet Cards (live credits from Beyond Presence API) */}
        {/* Top 5 Metric Cards (KPI Summary) */}
        <div className="kpi-grid">
          {/* Total Sessions */}
          <div className="kpi-card">
            <div className="kpi-icon-wrap icon-blue">
              <Coins size={22} />
            </div>
            <div className="kpi-content">
              <div className="kpi-label">Total Sessions</div>
              <div className="kpi-value">
                {summary ? formatIndianNumber(summary.total_sessions) : '—'}
              </div>
              <div className="kpi-trend positive">
                <TrendingUp size={12} />
                <span>{summary && summary.previous_period_change_percent != null ? `${summary.previous_period_change_percent > 0 ? '+' : ''}${summary.previous_period_change_percent}% vs previous month` : 'vs previous month'}</span>
              </div>
            </div>
          </div>

          {/* Active Students */}
          <div className="kpi-card">
            <div className="kpi-icon-wrap icon-cyan">
              <Users size={22} />
            </div>
            <div className="kpi-content">
              <div className="kpi-label">Active Students</div>
              <div className="kpi-value">
                {summary ? formatIndianNumber(summary.active_users || 0) : '—'}
              </div>
              <div className="kpi-subtext">Students taking avatar calls</div>
            </div>
          </div>

          {/* Enrolled Campuses */}
          <div className="kpi-card">
            <div className="kpi-icon-wrap icon-green">
              <Building2 size={22} />
            </div>
            <div className="kpi-content">
              <div className="kpi-label">Active Universities</div>
              <div className="kpi-value">
                {universities ? formatIndianNumber(universities.length || 1) : '—'}
              </div>
              <div className="kpi-subtext">Institutions with avatar access</div>
            </div>
          </div>

          {/* Total Minutes Used */}
          <div className="kpi-card">
            <div className="kpi-icon-wrap icon-purple">
              <Clock size={22} />
            </div>
            <div className="kpi-content">
              <div className="kpi-label">Total Minutes Used</div>
              <div className="kpi-value">
                {summary ? `${summary.total_minutes_used ?? 0} mins` : '—'}
              </div>
              <div className="kpi-subtext">
                Int: {summary?.interview_minutes ?? 0}m • Cou: {summary?.counsellor_minutes ?? 0}m • Tut: {summary?.tutor_minutes ?? 0}m
              </div>
            </div>
          </div>

          {/* Total Minutes Remaining */}
          <div className="kpi-card">
            <div className="kpi-icon-wrap icon-orange">
              <PiggyBank size={22} />
            </div>
            <div className="kpi-content">
              <div className="kpi-label">Total Minutes Remaining</div>
              <div className="kpi-value">
                {summary ? `${summary.total_minutes_remaining ?? 0} mins` : '—'}
              </div>
              <div className="kpi-subtext">
                Quota: {summary?.allocated_minutes ?? 0} mins total allocation
              </div>
            </div>
          </div>
        </div>

        {/* 3 Analytics Charts (3 Columns Layout) */}
        <div className="analytics-grid">
          {/* Chart 1: Dual-line Area/Line Chart */}
          <div className="analytics-card">
            <div className="analytics-header">
              <div>
                <h3 className="analytics-title">Session Usage Trend</h3>
                <span className="analytics-subtitle">{formatRangeLabel(filters.from, filters.to)}</span>
              </div>
              <div className="chart-legend-top">
                <span className="legend-chip">
                  <span className="chip-dot dot-student"></span> Total Sessions
                </span>
                <span className="legend-chip">
                  <span className="chip-dot dot-university"></span> Minutes Used
                </span>
              </div>
            </div>
            <div className="chart-container area-chart-wrap">
              <ResponsiveContainer width="100%" height={210}>
                <AreaChart
                  data={summary?.trend || []}
                  margin={{ top: 10, right: 10, left: -18, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="studentGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#168cf5" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#168cf5" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="universityGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(200, 215, 230, 0.4)" />
                  <XAxis
                    dataKey="display_date"
                    stroke="#8fa0b5"
                    fontSize={10}
                    tickLine={false}
                    interval={4}
                  />
                  <YAxis
                    stroke="#8fa0b5"
                    fontSize={10}
                    tickLine={false}
                    tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                  />
                  <Tooltip
                    formatter={(val, name) => [
                      name === 'duration_minutes' ? `${val} mins` : `${val} sessions`,
                      name === 'duration_minutes' ? 'Minutes Used' : 'Total Sessions'
                    ]}
                    labelFormatter={(lbl, payload) => payload?.[0]?.payload?.date || lbl}
                    contentStyle={{
                      background: '#ffffff',
                      border: '1px solid #dbe4ef',
                      borderRadius: '8px',
                      fontSize: '11px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="total_sessions"
                    stroke="#168cf5"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#studentGradient)"
                    dot={{ r: 4, strokeWidth: 2, fill: '#168cf5' }}
                    activeDot={{ r: 5 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="duration_minutes"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#universityGradient)"
                    dot={{ r: 4, strokeWidth: 2, fill: '#10b981' }}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chart 2: Feature Minutes Breakdown Donut Chart */}
          <div className="analytics-card">
            <div className="analytics-header">
              <div>
                <h3 className="analytics-title">Feature Minutes Breakdown</h3>
                <span className="analytics-subtitle">Minutes consumed by feature</span>
              </div>
              <span className="cost-total-badge" style={{ background: 'rgba(22, 140, 245, 0.1)', color: '#168cf5', fontWeight: 600 }}>
                {summary?.total_minutes_used ?? 0} mins
              </span>
            </div>
            <div className="donut-chart-layout">
              <div className="pie-wrap">
                {(summary?.total_minutes_used || 0) === 0 ? (
                  <svg viewBox="0 0 150 150" className="donut-ring-svg">
                    <circle cx="75" cy="75" r="52" fill="none" stroke="#eef2f7" strokeWidth="22" />
                  </svg>
                ) : (
                  <ResponsiveContainer width={150} height={150}>
                    <PieChart>
                      <Pie
                        data={featureMinutesData.filter((f) => f.minutes > 0)}
                        dataKey="minutes"
                        nameKey="name"
                        innerRadius={45}
                        outerRadius={68}
                        paddingAngle={3}
                      >
                        {featureMinutesData
                          .filter((f) => f.minutes > 0)
                          .map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                      </Pie>
                      <Tooltip
                        formatter={(val, name) => [`${val} mins`, name]}
                        contentStyle={{
                          background: '#ffffff',
                          border: '1px solid #dbe4ef',
                          borderRadius: '8px',
                          fontSize: '11px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
                <div className="donut-center-label">
                  <span className="center-amount">{summary?.total_minutes_used ?? 0}m</span>
                  <span className="center-sub">Total Mins</span>
                </div>
              </div>

              {/* Legend List */}
              <div className="service-legend-list">
                {featureMinutesData.map((f, idx) => (
                  <div className="service-legend-row" key={idx}>
                    <div className="service-name-wrap">
                      <span
                        className="service-dot"
                        style={{ background: f.color }}
                      ></span>
                      <span className="service-name">{f.name}</span>
                    </div>
                    <div className="service-amount-wrap">
                      <strong>{f.minutes}m</strong>
                      <span className="service-pct">({f.percentage}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Chart 3: Sessions by Agent Horizontal Bar Chart */}
          <div className="analytics-card">
            <div className="analytics-header">
              <div>
                <h3 className="analytics-title">Sessions by Agent</h3>
                <span className="analytics-subtitle">Actual models deployed</span>
              </div>
              <span className="models-count-chip">{models.length} Models</span>
            </div>
            {models.length > 0 && models.every((m) => !m.total_sessions) && (
              <div className="analytics-note">No per-model usage logged yet — appears once new AI calls are made</div>
            )}
            <div className="model-bars-container">
              {models.slice(0, 5).map((m, idx) => {
                const maxTokens = Math.max(...models.map((x) => x.total_sessions || 1), 1);
                const pct = Math.min(100, Math.round((m.total_sessions / maxTokens) * 100));
                return (
                  <div className="model-bar-item" key={idx}>
                    <div className="model-bar-top">
                      <div className="model-title-group">
                        <span className="model-badge-provider">{m.provider}</span>
                        <span className="model-name-text">{m.name}</span>
                      </div>
                      <span className="model-tokens-count">{formatIndianNumber(m.total_sessions)}</span>
                    </div>
                    <div className={`model-progress-track ${pct > 0 ? '' : 'track-empty'}`}>
                      <div
                        className="model-progress-fill"
                        style={{
                          width: `${pct}%`,
                          background:
                            idx === 0
                              ? 'linear-gradient(90deg, #168cf5, #38bdf8)'
                              : idx === 1
                              ? 'linear-gradient(90deg, #7557ee, #a855f7)'
                              : idx === 2
                              ? 'linear-gradient(90deg, #10b981, #34d399)'
                              : idx === 3
                              ? 'linear-gradient(90deg, #f59e0b, #fbbf24)'
                              : 'linear-gradient(90deg, #64748b, #94a3b8)'
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tabs Navigation Bar */}
        <div className="tabs-header-bar">
          <div className="tabs-buttons-list">
            <button
              className={`tab-item-btn ${activeTab === 'university' ? 'active' : ''}`}
              onClick={() => setActiveTab('university')}
            >
              <Building2 size={15} /> University Wise
            </button>
            <button
              className={`tab-item-btn ${activeTab === 'model' ? 'active' : ''}`}
              onClick={() => setActiveTab('model')}
            >
              <Cpu size={15} /> Model Wise
            </button>
            <button
              className={`tab-item-btn ${activeTab === 'service' ? 'active' : ''}`}
              onClick={() => setActiveTab('service')}
            >
              <Layers size={15} /> Service Wise
            </button>
            <button
              className={`tab-item-btn ${activeTab === 'student' ? 'active' : ''}`}
              onClick={() => setActiveTab('student')}
            >
              <Users size={15} /> Student Wise
            </button>
            <button
              className={`tab-item-btn ${activeTab === 'logs' ? 'active' : ''}`}
              onClick={() => setActiveTab('logs')}
            >
              Detailed Usage Logs
            </button>
          </div>

          <div className="tab-search-box">
            <Search size={14} className="tab-search-icon" />
            <input
              type="text"
              placeholder={`Search ${activeTab} data...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="tab-search-input"
            />
            {search && (
              <button className="clear-search-btn" onClick={() => setSearch('')}>
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Primary Data Tables */}
        <div className="primary-table-card">
          {/* TAB 1: UNIVERSITY WISE */}
          {activeTab === 'university' && (
            <div className="table-responsive-wrapper">
              <table className="costing-data-table">
                <thead>
                  <tr>
                    <th style={{ width: '45px' }}>#</th>
                    <th onClick={() => handleSort('university_name')} style={{ cursor: 'pointer' }}>
                      University Name <ArrowUpDown size={12} style={{ marginLeft: 4 }} />
                    </th>
                    <th onClick={() => handleSort('students_count')} style={{ cursor: 'pointer' }}>
                      Students Count <ArrowUpDown size={12} style={{ marginLeft: 4 }} />
                    </th>
                    <th onClick={() => handleSort('total_sessions')} style={{ cursor: 'pointer' }}>
                      Total Sessions <ArrowUpDown size={12} style={{ marginLeft: 4 }} />
                    </th>
                    <th onClick={() => handleSort('minutes_used')} style={{ cursor: 'pointer' }}>
                      Minutes Used <ArrowUpDown size={12} style={{ marginLeft: 4 }} />
                    </th>
                    <th onClick={() => handleSort('minutes_remaining')} style={{ cursor: 'pointer' }}>
                      Minutes Remaining <ArrowUpDown size={12} style={{ marginLeft: 4 }} />
                    </th>
                    <th>Feature Breakdown</th>
                    <th style={{ width: '90px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUniRows.length > 0 ? (
                    filteredUniRows.map((r, i) => (
                      <tr key={r.id || i}>
                        <td>{i + 1}</td>
                        <td>
                          <div className="uni-cell-name">
                            <Building2 size={14} className="cell-uni-icon" />
                            <strong>{r.university_name}</strong>
                          </div>
                        </td>
                        <td>
                          <span className="badge-count">{r.students_count} students</span>
                        </td>
                        <td>
                          <strong>{formatIndianNumber(r.total_sessions)}</strong>
                        </td>
                        <td>
                          <span className="cost-usd-cell" style={{ fontWeight: 600 }}>
                            {r.minutes_used ?? 0}m
                          </span>
                        </td>
                        <td>
                          <span
                            className="cost-inr-cell"
                            style={{
                              fontWeight: 600,
                              color: (r.minutes_remaining < 30 ? '#ef4444' : '#10b981')
                            }}
                          >
                            {r.minutes_remaining ?? 0}m
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            <span
                              className="badge-count"
                              style={{
                                fontSize: '11px',
                                background: 'rgba(56, 189, 248, 0.15)',
                                color: '#0284c7'
                              }}
                            >
                              Interview: {r.interview_minutes ?? 0}m
                            </span>
                            <span
                              className="badge-count"
                              style={{
                                fontSize: '11px',
                                background: 'rgba(168, 85, 247, 0.15)',
                                color: '#7e22ce'
                              }}
                            >
                              Counsellor: {r.counsellor_minutes ?? 0}m
                            </span>
                            <span
                              className="badge-count"
                              style={{
                                fontSize: '11px',
                                background: 'rgba(16, 185, 129, 0.15)',
                                color: '#047857'
                              }}
                            >
                              Tutor: {r.tutor_minutes ?? 0}m
                            </span>
                          </div>
                        </td>
                        <td>
                          <button
                            className="table-action-view-btn"
                            onClick={() => openUniversityModal(r)}
                            title="View University Breakdown"
                          >
                            <Eye size={13} /> View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="no-data-cell">
                        No universities found matching your filter criteria.
                      </td>
                    </tr>
                  )}
                </tbody>

                {/* PINNED TOTAL SUMMARY ROW */}
                {uniCostingData.total_summary && (
                  <tfoot>
                    <tr className="pinned-total-row">
                      <td>∑</td>
                      <td>
                        <strong>{uniCostingData.total_summary.university_name}</strong>
                      </td>
                      <td>
                        <strong className="badge-count total-badge">
                          {uniCostingData.total_summary.students_count} students
                        </strong>
                      </td>
                      <td>
                        <strong className="total-highlight">
                          {formatIndianNumber(uniCostingData.total_summary.total_sessions)}
                        </strong>
                      </td>
                      <td>
                        <strong className="cost-usd-highlight">
                          {uniCostingData.total_summary.minutes_used ?? 0}m
                        </strong>
                      </td>
                      <td>
                        <strong className="cost-inr-highlight">
                          {uniCostingData.total_summary.minutes_remaining ?? 0}m
                        </strong>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          <span className="badge-count" style={{ fontSize: '11px' }}>
                            Int: {uniCostingData.total_summary.interview_minutes ?? 0}m
                          </span>
                          <span className="badge-count" style={{ fontSize: '11px' }}>
                            Cou: {uniCostingData.total_summary.counsellor_minutes ?? 0}m
                          </span>
                          <span className="badge-count" style={{ fontSize: '11px' }}>
                            Tut: {uniCostingData.total_summary.tutor_minutes ?? 0}m
                          </span>
                        </div>
                      </td>
                      <td>—</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {/* TAB 2: MODEL WISE */}
          {activeTab === 'model' && (
            <div className="table-responsive-wrapper">
              <table className="costing-data-table">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Feature / Agent Name</th>
                    <th>Actual Agent ID</th>
                    <th>Total Sessions</th>
                    <th>Minutes Used</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredModelRows.map((m, i) => (
                    <tr key={i}>
                      <td>
                        <span className="provider-pill">{m.provider}</span>
                      </td>
                      <td>
                        <strong>{m.name}</strong>
                      </td>
                      <td>
                        <code className="code-id">{m.id}</code>
                      </td>
                      <td>
                        <strong>{formatIndianNumber(m.total_sessions)}</strong>
                      </td>
                      <td>
                        <span className="cost-usd-cell" style={{ fontWeight: 600 }}>
                          {m.minutes_used ?? 0}m
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 3: SERVICE WISE */}
          {activeTab === 'service' && (
            <div className="table-responsive-wrapper">
              <table className="costing-data-table">
                <thead>
                  <tr>
                    <th>AI Feature / Service</th>
                    <th>Total Sessions</th>
                    <th>Minutes Used</th>
                    <th>% of Minutes Consumed</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredServiceRows.map((s, i) => (
                    <tr key={i}>
                      <td>
                        <div className="uni-cell-name">
                          <Layers size={14} className="cell-uni-icon" />
                          <strong>{s.service_name}</strong>
                        </div>
                      </td>
                      <td>{formatIndianNumber(s.total_sessions)}</td>
                      <td>
                        <span className="cost-usd-cell" style={{ fontWeight: 600 }}>
                          {s.minutes_used ?? 0}m
                        </span>
                      </td>
                      <td>
                        <div className="pct-progress-cell">
                          <div className="pct-bar">
                            <span style={{ width: `${s.percentage}%` }}></span>
                          </div>
                          <span>{s.percentage}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 4: STUDENT WISE */}
          {activeTab === 'student' && (
            <div className="table-responsive-wrapper">
              <table className="costing-data-table">
                <thead>
                  <tr>
                    <th style={{ width: '45px' }}>#</th>
                    <th onClick={() => handleSort('student_name')} style={{ cursor: 'pointer' }}>
                      Student Name <ArrowUpDown size={12} style={{ marginLeft: 4 }} />
                    </th>
                    <th onClick={() => handleSort('university_name')} style={{ cursor: 'pointer' }}>
                      University <ArrowUpDown size={12} style={{ marginLeft: 4 }} />
                    </th>
                    <th onClick={() => handleSort('minutes_used')} style={{ cursor: 'pointer' }}>
                      Minutes Used <ArrowUpDown size={12} style={{ marginLeft: 4 }} />
                    </th>
                    <th onClick={() => handleSort('minutes_remaining')} style={{ cursor: 'pointer' }}>
                      Minutes Remaining <ArrowUpDown size={12} style={{ marginLeft: 4 }} />
                    </th>
                    <th>Feature Breakdown</th>
                    <th onClick={() => handleSort('last_active_date')} style={{ cursor: 'pointer' }}>
                      Last Active <ArrowUpDown size={12} style={{ marginLeft: 4 }} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudentRows.length > 0 ? (
                    filteredStudentRows.map((st, i) => (
                      <tr key={st.student_id || i}>
                        <td>{i + 1}</td>
                        <td>
                          <strong>{st.student_name}</strong>
                        </td>
                        <td>{st.university_name}</td>
                        <td>
                          <span className="cost-usd-cell" style={{ fontWeight: 600 }}>
                            {st.minutes_used ?? 0}m
                          </span>
                        </td>
                        <td>
                          <span
                            className="cost-inr-cell"
                            style={{
                              fontWeight: 600,
                              color: (st.minutes_remaining < 15 ? '#ef4444' : '#10b981')
                            }}
                          >
                            {st.minutes_remaining ?? 0}m / {st.allocated_minutes ?? 60}m
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            <span
                              className="badge-count"
                              style={{
                                fontSize: '11px',
                                background: 'rgba(56, 189, 248, 0.15)',
                                color: '#0284c7'
                              }}
                            >
                              Interview: {st.interview_minutes ?? 0}m
                            </span>
                            <span
                              className="badge-count"
                              style={{
                                fontSize: '11px',
                                background: 'rgba(168, 85, 247, 0.15)',
                                color: '#7e22ce'
                              }}
                            >
                              Counsellor: {st.counsellor_minutes ?? 0}m
                            </span>
                            <span
                              className="badge-count"
                              style={{
                                fontSize: '11px',
                                background: 'rgba(16, 185, 129, 0.15)',
                                color: '#047857'
                              }}
                            >
                              Tutor: {st.tutor_minutes ?? 0}m
                            </span>
                          </div>
                        </td>
                        <td>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>
                            {st.last_active_date ? new Date(st.last_active_date).toLocaleDateString() : '—'}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="no-data-cell">
                        No student records found matching your filter criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 5: DETAILED USAGE LOGS */}
          {activeTab === 'logs' && (
            <div className="table-responsive-wrapper">
              <table className="costing-data-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>University</th>
                    <th>User</th>
                    <th>Service / Feature</th>
                    <th>Model / Engine</th>
                    <th>Total Sessions</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogRows.slice(0, 100).map((l, i) => (
                    <tr key={l.id || i}>
                      <td>{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</td>
                      <td>{l.university_name}</td>
                      <td>
                        <span
                          className={`user-type-tag ${l.user_type === 'STUDENT' ? 'tag-student' : 'tag-uni'}`}
                        >
                          {l.user_type === 'STUDENT' ? l.student_name || 'Student' : 'University'}
                        </span>
                      </td>
                      <td>{l.service_name}</td>
                      <td>{l.model_name || l.actual_model_id}</td>
                      <td>
                        <strong>{formatIndianNumber(l.total_sessions)}</strong>
                      </td>
                      <td>
                        <span className="cost-usd-cell" style={{ fontWeight: 600 }}>
                          {l.duration_minutes ?? 0}m
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 3 Bottom Granular Cards */}
        <div className="bottom-cards-grid">
          {/* Card 1: University AI Services Usage */}
          <div className="bottom-card">
            <div className="bottom-card-header">
              <h4 className="bottom-card-title">University AI Services Usage</h4>
              <select
                value={selectedBottomUni}
                onChange={(e) => setSelectedBottomUni(e.target.value)}
                className="bottom-uni-picker"
              >
                {universities.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="bottom-table-wrap">
              <table className="bottom-compact-table">
                <thead>
                  <tr>
                    <th>Service / Feature</th>
                    <th>Sessions</th>
                    <th>Minutes Used</th>
                  </tr>
                </thead>
                <tbody>
                  {bottomUniServices.length > 0 ? (
                    bottomUniServices.map((x, i) => (
                      <tr key={i}>
                        <td>
                          <strong>{x.service_name}</strong>
                        </td>
                        <td>{formatIndianNumber(x.sessions)}</td>
                        <td>
                          <span className="cost-usd-cell" style={{ fontWeight: 600 }}>
                            {x.minutes ?? 0}m
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="no-data-cell">
                        No usage recorded for this university.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Card 2: Student Usage */}
          <div className="bottom-card">
            <div className="bottom-card-header">
              <h4 className="bottom-card-title">Top Student Usage</h4>
              <span className="bottom-card-subtitle">By minutes consumed</span>
            </div>
            <div className="bottom-table-wrap">
              <table className="bottom-compact-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>University</th>
                    <th>Sessions</th>
                    <th>Minutes Used</th>
                    <th>Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {bottomStudents.map((st, i) => (
                    <tr key={i}>
                      <td>
                        <strong>{st.student_name}</strong>
                      </td>
                      <td>
                        <span className="uni-subname">{st.university_name}</span>
                      </td>
                      <td>{formatIndianNumber(st.total_sessions)}</td>
                      <td>
                        <span className="cost-usd-cell" style={{ fontWeight: 600 }}>
                          {st.minutes_used ?? 0}m
                        </span>
                      </td>
                      <td>
                        <span
                          className="cost-inr-cell"
                          style={{
                            fontWeight: 600,
                            color: (st.minutes_remaining < 15 ? '#ef4444' : '#10b981')
                          }}
                        >
                          {st.minutes_remaining ?? 0}m
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Card 3: Model Usage */}
          <div className="bottom-card">
            <div className="bottom-card-header">
              <h4 className="bottom-card-title">Model Usage</h4>
              <span className="bottom-card-subtitle">Active LLM engines</span>
            </div>
            <div className="bottom-table-wrap">
              <table className="bottom-compact-table">
                <thead>
                  <tr>
                    <th>Feature / Agent</th>
                    <th>Provider</th>
                    <th>Sessions</th>
                    <th>Minutes Used</th>
                  </tr>
                </thead>
                <tbody>
                  {bottomModels.map((m, i) => (
                    <tr key={i}>
                      <td>
                        <strong>{m.name}</strong>
                      </td>
                      <td>
                        <span className="provider-subpill">{m.provider}</span>
                      </td>
                      <td>{formatIndianNumber(m.total_sessions)}</td>
                      <td>
                        <span className="cost-usd-cell" style={{ fontWeight: 600 }}>
                          {m.minutes_used ?? 0}m
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer Bar */}
        <div className="costing-footer-strip">
          <div className="footer-left-note">
            <Info size={14} className="footer-info-icon" />
            <span>
              Beyond Presence Studio integration tracks live call sessions and computes student minute utilization. Usage is updated in real time via webhooks.
            </span>
          </div>
          <div className="footer-right-rates">
            <span className="rate-item">
              Default Quota: <strong>60 mins / student</strong>
            </span>
            <span className="sep-divider">|</span>
            <span className="rate-item">
              Last Updated:{' '}
              <strong>
                {summary?.last_updated
                  ? new Date(summary.last_updated).toLocaleDateString()
                  : '—'}
              </strong>
            </span>
          </div>
        </div>
      </div>

      {/* University Modal */}
      {modalOpen && modalUniversity && (
        <div
          className="modal-overlay-bg"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModalOpen(false);
          }}
        >
          <div className="uni-details-modal">
            <div className="modal-top-bar">
              <div className="modal-title-wrap">
                <Building2 size={22} className="modal-building-icon" />
                <div>
                  <h3 className="modal-heading">{modalUniversity.university_name}</h3>
                  <p className="modal-subheading">
                    Detailed AI Consumption, Minutes &amp; Feature Breakdown
                  </p>
                </div>
              </div>
              <button className="modal-close-icon-btn" onClick={() => setModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body-content">
              {/* Modal Mini KPIs */}
              <div className="modal-kpi-row">
                <div className="modal-mini-kpi">
                  <span className="mini-label">Total Sessions</span>
                  <strong className="mini-val">{formatIndianNumber(modalUniversity.total_sessions || modalStats.sessions || 0)}</strong>
                </div>
                <div className="modal-mini-kpi">
                  <span className="mini-label">Minutes Used</span>
                  <strong className="mini-val cost-usd-cell">{modalUniversity.minutes_used ?? 0}m</strong>
                </div>
                <div className="modal-mini-kpi">
                  <span className="mini-label">Minutes Remaining</span>
                  <strong className="mini-val cost-inr-cell">{modalUniversity.minutes_remaining ?? 0}m</strong>
                </div>
                <div className="modal-mini-kpi">
                  <span className="mini-label">Feature Breakdown</span>
                  <strong className="mini-val" style={{ fontSize: '12px' }}>
                    Int: {modalUniversity.interview_minutes ?? 0}m • Cou: {modalUniversity.counsellor_minutes ?? 0}m • Tut: {modalUniversity.tutor_minutes ?? 0}m
                  </strong>
                </div>
              </div>

              <div className="modal-table-section">
                <h4 className="modal-section-title">Recent Usage Logs for this University</h4>
                <div className="modal-table-scroll">
                  <table className="costing-data-table modal-inner-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>User Type</th>
                        <th>Feature / Service</th>
                        <th>Model / Engine</th>
                        <th>Total Sessions</th>
                        <th>Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalStats.logs.slice(0, 40).map((x, i) => (
                        <tr key={i}>
                          <td>{x.created_at ? new Date(x.created_at).toLocaleDateString() : '—'}</td>
                          <td>
                            <span
                              className={`user-type-tag ${
                                x.user_type === 'STUDENT' ? 'tag-student' : 'tag-uni'
                              }`}
                            >
                              {x.user_type === 'STUDENT' ? x.student_name || 'Student' : 'University'}
                            </span>
                          </td>
                          <td>{x.service_name}</td>
                          <td>{x.model_name || x.actual_model_id}</td>
                          <td>
                            <strong>{formatIndianNumber(x.total_sessions)}</strong>
                          </td>
                          <td>
                            <span className="cost-usd-cell" style={{ fontWeight: 600 }}>
                              {x.duration_minutes ?? 0}m
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default BeyondPresenceCosting;
